import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { hideSpinnersStyle, ITEM_TYPES, OBJECT_TYPES } from './constants';
import {
  ChevronDown, ChevronRight, Phone, Mail,
  FileText, Image, Lock, ExternalLink,
  CheckCircle2, MessageCircle,
  Wrench, Package, Truck, Settings
} from 'lucide-react';
import ChatModal from './ChatModal';

const ClientViewV2 = ({ shareToken }) => {
  // Визначаємо чи колір темний — для авто-контрасту тексту
  const isDark = (hex) => {
    if (!hex) return false;
    const c = hex.replace('#', '');
    const r = parseInt(c.substr(0, 2), 16);
    const g = parseInt(c.substr(2, 2), 16);
    const b = parseInt(c.substr(4, 2), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 < 140;
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [project, setProject] = useState(null);
  const [shareData, setShareData] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [expandedEstimates, setExpandedEstimates] = useState({});
  const [showChat, setShowChat] = useState(false);
  const [mode, setMode] = useState('all'); // 'all' | 'done' | 'paid'
  const [reportsListOpen, setReportsListOpen] = useState(false);
  const [reportModalId, setReportModalId] = useState(null); // id звіту що відкритий, null = закрито

  // Блокування body scroll коли будь-яка модалка відкрита
  useEffect(() => {
    const anyOpen = reportsListOpen || reportModalId !== null;
    if (anyOpen) {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = original; };
    }
  }, [reportsListOpen, reportModalId]);

  // Завантаження даних
  useEffect(() => {
    loadSharedProject();
  }, [shareToken]);

  const loadSharedProject = async () => {
    try {
      setLoading(true);
      
      // 1. Отримуємо share запис
      const { data: share, error: shareError } = await supabase
        .from('shared_projects')
        .select('*')
        .eq('share_token', shareToken)
        .single();
      
      if (shareError || !share) {
        setError('Посилання недійсне або проєкт не знайдено');
        setLoading(false);
        return;
      }

      // Fallback брендингу: якщо в snapshot нема ключових полів — читаємо з user_profiles
      // (потрібно для проектів розшарених до появи brand_settings у shared_projects)
      const hasBrandSnapshot = share.brand_settings && (
        share.brand_settings.companyName ||
        share.brand_settings.managerName ||
        share.brand_settings.logo ||
        share.brand_settings.phone ||
        share.brand_settings.email
      );
      if (!hasBrandSnapshot) {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('brand_name, display_name, phone, email, brand_logo_url, website, instagram')
          .eq('user_id', share.user_id)
          .maybeSingle();
        if (profile) {
          share.brand_settings = {
            ...(share.brand_settings || {}),
            companyName: profile.brand_name || '',
            managerName: profile.display_name || '',
            phone: profile.phone || '',
            email: profile.email || '',
            logo: profile.brand_logo_url || '',
            website: profile.website || '',
            instagram: profile.instagram || ''
          };
        }
      }

      setShareData(share);
      
      // Перевірка чи посилання активне
      if (share.is_active === false) {
        setError('inactive');
        setLoading(false);
        return;
      }
      
      // 2. Якщо locked — показуємо snapshot
      if (share.is_locked && share.locked_snapshot) {
        setProject(share.locked_snapshot);
        setLoading(false);
        return;
      }
      
      // 3. Якщо unlocked — завантажуємо live дані
      const { data: projectRow, error: projectError } = await supabase
        .from('projects')
        .select('data')
        .eq('user_id', share.user_id)
        .eq('data->>id', String(share.project_id));
      
      if (projectError || !projectRow || projectRow.length === 0) {
        // Пробуємо альтернативний спосіб
        const { data: allProjects, error: allError } = await supabase
          .from('projects')
          .select('data')
          .eq('user_id', share.user_id);
        
        if (allError || !allProjects) {
          setError('Проєкт не знайдено');
          setLoading(false);
          return;
        }
        
        const found = allProjects.find(row => String(row.data?.id) === String(share.project_id));
        if (found) {
          setProject(found.data);
        } else {
          setError('Проєкт не знайдено');
        }
      } else {
        setProject(projectRow[0].data);
      }
      
      setLoading(false);
    } catch (e) {
      console.error('Помилка завантаження:', e);
      setError('Помилка завантаження даних');
      setLoading(false);
    }
  };

  const toggleSection = (estimateId, sectionId) => {
    const key = `${estimateId}-${sectionId}`;
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleEstimate = (estimateId) => {
    setExpandedEstimates(prev => ({ ...prev, [estimateId]: !prev[estimateId] }));
  };

  // Розрахунок підсумків кошторису
  const calcEstimateTotals = (estimate) => {
    let totalClientCost = 0;
    let atsAmount = 0;
    const atsEnabled = estimate.atsEnabled ?? true;
    const atsPercent = estimate.atsPercent ?? 5;

    (estimate.items || []).forEach(section => {
      let sectionClientCost = 0;
      (section.items || []).forEach(item => {
        if (item.hidden) return;
        const cp = item.clientPrice !== undefined 
          ? item.clientPrice 
          : Math.round((item.workPrice || 0) * (1 + (item.markup || 0) / 100) * 100) / 100;
        sectionClientCost += Math.round((item.qty || 0) * cp * 100) / 100;
      });
      totalClientCost += sectionClientCost;
      if (atsEnabled && (section.includeATS ?? true)) {
        atsAmount += sectionClientCost * atsPercent / 100;
      }
    });

    return {
      totalClientCost: Math.round(totalClientCost * 100) / 100,
      atsAmount: Math.round(atsAmount * 100) / 100,
      grandTotal: Math.round((totalClientCost + atsAmount) * 100) / 100
    };
  };

  // Розрахунок підсумків секції
  const calcSectionTotals = (section) => {
    let clientCost = 0;
    (section.items || []).forEach(item => {
      if (item.hidden) return;
      const cp = item.clientPrice !== undefined 
        ? item.clientPrice 
        : Math.round((item.workPrice || 0) * (1 + (item.markup || 0) / 100) * 100) / 100;
      clientCost += Math.round((item.qty || 0) * cp * 100) / 100;
    });
    return { clientCost: Math.round(clientCost * 100) / 100 };
  };

  // Форматування оплат
  const getEstimatePayments = (estimate) => {
    return (estimate.payments || []).sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const getEstimateReceived = (estimate) => {
    return (estimate.payments || []).reduce((sum, p) => sum + (p.amount || 0), 0);
  };

  // Розподіл витрат по типах (з усіх Звітів, без Попереднього). АТС окремо.
  // Дзеркалить getProjectExpensesByType з EstimateScreen.js:859 (навмисна копія — щоб не чіпати EstimateScreen).
  const getProjectExpensesByType = (proj) => {
    const typeExpenses = {
      'Робота': 0,
      'Матеріали': 0,
      'Комплектація': 0,
      'Транспорт': 0,
      'Інші послуги': 0,
      'АТС': 0
    };
    let grandTotal = 0;
    (proj?.estimates || []).forEach(est => {
      if (est.isPreliminary) return;
      let estimateSubtotal = 0;
      (est.items || []).forEach(section => {
        let sectionTotal = 0;
        (section.items || []).forEach(item => {
          if (!item.hidden) {
            const calculatedCp = Math.round((item.workPrice || 0) * (1 + (item.markup || 0) / 100) * 100) / 100;
            const cp = item.clientPrice !== undefined ? item.clientPrice : calculatedCp;
            const cost = Math.round((item.qty || 0) * cp * 100) / 100;
            const type = item.type || 'Робота';
            if (typeExpenses.hasOwnProperty(type) && type !== 'АТС') {
              typeExpenses[type] += cost;
            } else if (type !== 'АТС') {
              typeExpenses['Інші послуги'] += cost;
            }
            grandTotal += cost;
            if (section.includeATS ?? true) {
              sectionTotal += cost;
            }
          }
        });
        estimateSubtotal += sectionTotal;
      });
      if (est.atsEnabled ?? true) {
        const atsAmount = estimateSubtotal * (est.atsPercent || 0) / 100;
        typeExpenses['АТС'] += atsAmount;
        grandTotal += atsAmount;
      }
    });
    return { typeExpenses, grandTotal };
  };

  // --- РЕНДЕР ---

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <style>{hideSpinnersStyle}</style>
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-gray-300 border-t-gray-600 rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-sm text-gray-500">Завантаження...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <style>{hideSpinnersStyle}</style>
        <div className="text-center max-w-md mx-auto px-6">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-7 h-7 text-gray-400" />
          </div>
          {error === 'inactive' ? (
            <>
              <h1 className="text-lg font-semibold text-gray-900 mb-2">Доступ до проєкту закрито</h1>
              <p className="text-sm text-gray-500">Власник проєкту тимчасово обмежив доступ до перегляду. Зверніться до менеджера проєкту для отримання актуального посилання.</p>
            </>
          ) : (
            <>
              <h1 className="text-lg font-semibold text-gray-900 mb-2">Посилання недійсне</h1>
              <p className="text-sm text-gray-500">{error}</p>
            </>
          )}
        </div>
      </div>
    );
  }

  if (!project) return null;

  const objectType = OBJECT_TYPES[project.objectType] || OBJECT_TYPES.other;

  // У списку показуємо ТІЛЬКИ попередній кошторис (звіти доступні через модалку).
  // Позиції всередині фільтруються за mode (all/done/paid).
  const allEstimates = project.estimates || [];
  const visibleEstimates = allEstimates.filter(est => est.isPreliminary);

  // Counts обчислюємо нижче — після визначення getPreliminaryItemStatus

  // === Метрики для Dashboard ===
  const preliminary = (project.estimates || []).find(e => e.isPreliminary);
  const reports = (project.estimates || []).filter(e => !e.isPreliminary);
  const budgetPreliminary = preliminary ? calcEstimateTotals(preliminary).grandTotal : 0;
  let totalExpenses = 0;
  let totalReceived = 0;
  const allPayments = [];
  reports.forEach(est => {
    totalExpenses += calcEstimateTotals(est).grandTotal;
    totalReceived += getEstimateReceived(est);
    (est.payments || []).forEach(p => {
      allPayments.push({ ...p, estimateName: est.name });
    });
  });
  allPayments.sort((a, b) => new Date(a.date) - new Date(b.date));
  const balance = totalReceived - totalExpenses;
  const allPreliminaryItems = preliminary ? (preliminary.items || []).flatMap(s => s.items || []) : [];
  const totalItemsCount = allPreliminaryItems.length;
  const completedItemsCount = allPreliminaryItems.filter(i => i.completed).length;
  const workPercent = totalItemsCount > 0 ? Math.round(completedItemsCount / totalItemsCount * 100) : 0;

  // Скроли з клікабельних карток Огляду
  const scrollToPreliminary = () => {
    const el = document.getElementById('preliminary-block');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const scrollToPayments = () => {
    const el = document.getElementById('payments-block');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const openReportsModal = () => {
    if (reports.length === 1) {
      // Якщо один звіт — одразу відкриваємо його
      setReportModalId(reports[0].id);
    } else if (reports.length > 1) {
      // Кілька — показуємо список для вибору
      setReportsListOpen(true);
    }
  };
  const openReportById = (id) => {
    setReportsListOpen(false);
    setReportModalId(id);
  };
  const fmt = (n) => Math.round(n).toLocaleString('uk-UA');

  // === Вартість м² ===
  const area = project.area || 0;
  const plannedCostPerM2 = area > 0 ? budgetPreliminary / area : 0;
  const currentCostPerM2 = area > 0 ? totalExpenses / area : 0;
  const m2Delta = currentCostPerM2 - plannedCostPerM2;

  // === Donut розподіл витрат по 4 об'єднаних категоріях ===
  const expByType = getProjectExpensesByType(project);
  const categorized = {
    'Роботи':     expByType.typeExpenses['Робота'] || 0,
    'Матеріали':  (expByType.typeExpenses['Матеріали'] || 0) + (expByType.typeExpenses['Комплектація'] || 0),
    'Послуги':    (expByType.typeExpenses['Інші послуги'] || 0) + (expByType.typeExpenses['АТС'] || 0),
    'Транспортні витрати': expByType.typeExpenses['Транспорт'] || 0,
  };
  const catTotal = Object.values(categorized).reduce((s, v) => s + v, 0);
  const catColors = {
    'Роботи':     '#163248',
    'Матеріали':  '#f97316',
    'Послуги':    '#22c55e',
    'Транспортні витрати': '#a3a3a3',
  };
  const catSegments = Object.entries(categorized)
    .map(([name, val]) => ({ name, val, pct: catTotal > 0 ? (val / catTotal * 100) : 0, color: catColors[name] }))
    .filter(seg => seg.val > 0)
    .sort((a, b) => b.val - a.val);
  // conic-gradient string з cumulative %
  let cumulative = 0;
  const donutBg = catSegments.length > 0
    ? catSegments.map(seg => {
        const start = cumulative;
        cumulative += seg.pct;
        return `${seg.color} ${start}% ${cumulative}%`;
      }).join(', ')
    : '#e5e5e5 0% 100%';

  // === Плановий розподіл (з попереднього кошторису) для блоку "Розподіл (план / поточне)" ===
  const planCategorized = { 'Роботи': 0, 'Матеріали': 0, 'Транспортні витрати': 0, 'Послуги': 0 };
  if (preliminary) {
    let atsBase = 0;
    (preliminary.items || []).forEach(section => {
      (section.items || []).forEach(item => {
        if (!item.hidden) {
          const cp = item.clientPrice !== undefined ? item.clientPrice : Math.round((item.workPrice || 0) * (1 + (item.markup || 0) / 100) * 100) / 100;
          const cost = (item.qty || 0) * cp;
          const type = item.type || 'Робота';
          if (type === 'Робота') planCategorized['Роботи'] += cost;
          else if (type === 'Матеріали' || type === 'Комплектація') planCategorized['Матеріали'] += cost;
          else if (type === 'Транспорт') planCategorized['Транспортні витрати'] += cost;
          else planCategorized['Послуги'] += cost;
          if (section.includeATS ?? true) atsBase += cost;
        }
      });
    });
    if (preliminary.atsEnabled ?? true) {
      planCategorized['Послуги'] += atsBase * (preliminary.atsPercent || 0) / 100;
    }
  }
  const categoriesForBar = [
    { name: 'Роботи',                icon: Wrench,   plan: planCategorized['Роботи'],                current: categorized['Роботи'] },
    { name: 'Матеріали',             icon: Package,  plan: planCategorized['Матеріали'],             current: categorized['Матеріали'] },
    { name: 'Транспортні витрати',   icon: Truck,    plan: planCategorized['Транспортні витрати'],   current: categorized['Транспортні витрати'] },
    { name: 'Послуги',               icon: Settings, plan: planCategorized['Послуги'],               current: categorized['Послуги'] },
  ].filter(c => c.plan > 0 || c.current > 0);
  const hasAnyDistribution = categoriesForBar.length > 0;

  // === FIFO покриття звітів (для визначення "оплаченості" позицій) ===
  // Всі платежі проекту сумуються, розподіляються по звітах у порядку створення.
  // Звіт вважається "покритим" тільки якщо його total повністю покритий.
  // Плюс FIFO-значення "оплачено X з Y" — скільки з totalReceived припадає на цей звіт.
  const estimateCoverageMap = {};
  const estimateFifoReceivedMap = {};
  {
    const sortedReports = reports.slice().sort((a, b) => (a.id || 0) - (b.id || 0));
    let remainingPayments = totalReceived;
    sortedReports.forEach(est => {
      const total = calcEstimateTotals(est).grandTotal;
      const covered = Math.min(total, remainingPayments);
      estimateFifoReceivedMap[est.id] = covered;
      estimateCoverageMap[est.id] = covered >= total;
      remainingPayments = Math.max(0, remainingPayments - covered);
    });
  }

  // Для позиції preliminary обчислює сумарний факт з linked звіт-позицій.
  // Повертає { factQty, factCost, linkedItems: [{estName, secName, item, qty, cost}] }
  const getPreliminaryItemFactData = (prItem) => {
    const links = prItem?.links || [];
    let factQty = 0;
    let factCost = 0;
    const linkedItems = [];
    links.forEach(link => {
      const est = (project.estimates || []).find(e => e.id === link.estimateId);
      const sec = est?.items?.find(s => s.id === link.sectionId);
      const rItem = sec?.items?.find(i => i.id === link.itemId);
      if (!rItem || rItem.hidden) return;
      const cp = rItem.clientPrice !== undefined
        ? rItem.clientPrice
        : Math.round((rItem.workPrice || 0) * (1 + (rItem.markup || 0) / 100) * 100) / 100;
      const qty = rItem.qty || 0;
      const cost = qty * cp;
      factQty += qty;
      factCost += cost;
      linkedItems.push({ estName: est?.name || '?', secName: sec?.name || '?', item: rItem, qty, cost });
    });
    return { factQty, factCost, linkedItems };
  };

  // Preliminary-total метрики (для банера "Разом за попереднім кошторисом")
  // planCost вже включає АТС (з calcEstimateTotals). factCost треба також додати АТС.
  const preliminaryPlanCost = budgetPreliminary;
  let preliminaryFactCostRaw = 0;
  let preliminaryFactCostATSBase = 0; // база для розрахунку АТС (сума факту з секцій де includeATS=true)
  if (preliminary) {
    (preliminary.items || []).forEach(sec => {
      const secIncludeATS = sec.includeATS ?? true;
      (sec.items || []).forEach(it => {
        if (it.hidden) return;
        const itemFactCost = getPreliminaryItemFactData(it).factCost;
        preliminaryFactCostRaw += itemFactCost;
        if (secIncludeATS) preliminaryFactCostATSBase += itemFactCost;
      });
    });
  }
  // Додаємо АТС до факту в тому ж розмірі як в preliminary
  const prelimAtsEnabled = preliminary?.atsEnabled ?? true;
  const prelimAtsPercent = preliminary?.atsPercent ?? 0;
  const preliminaryFactATS = (prelimAtsEnabled ? preliminaryFactCostATSBase * prelimAtsPercent / 100 : 0);
  const preliminaryFactCost = preliminaryFactCostRaw + preliminaryFactATS;

  // Функція: сума позицій розділу preliminary з урахуванням АТС (для шапки розділу)
  const getSectionCostWithATS = (section) => {
    if (!preliminary) return { clientCost: 0, atsAmount: 0, total: 0 };
    let clientCost = 0;
    (section.items || []).forEach(it => {
      if (it.hidden) return;
      const cp = it.clientPrice !== undefined ? it.clientPrice : Math.round((it.workPrice || 0) * (1 + (it.markup || 0) / 100) * 100) / 100;
      clientCost += (it.qty || 0) * cp;
    });
    const includeATS = section.includeATS ?? true;
    const atsAmount = (prelimAtsEnabled && includeATS) ? clientCost * prelimAtsPercent / 100 : 0;
    return { clientCost, atsAmount, total: clientCost + atsAmount };
  };

  // Для позиції в preliminary визначає її статус виконання і оплати
  // на основі links[] (додаються через модалку в EstimateScreen).
  const getPreliminaryItemStatus = (prItem) => {
    const links = prItem?.links || [];
    // Legacy: якщо links немає, але item.completed=true (старий ручний прапорець) —
    // вважаємо повне виконання без інформації про оплату.
    if (links.length === 0) {
      if (prItem?.completed) return { execution: 'full', payment: 'none' };
      return { execution: 'none', payment: 'none' };
    }
    // Виконання: якщо є хоч один isPartial=true → часткове. Інакше повне.
    const hasPartial = links.some(l => l.isPartial);
    const execution = hasPartial ? 'partial' : 'full';
    // Оплата: скільки з linked звітів покриті FIFO
    const coveredCount = links.filter(l => estimateCoverageMap[l.estimateId]).length;
    let payment;
    if (coveredCount === 0) payment = 'none';
    else if (coveredCount === links.length) payment = 'full';
    else payment = 'partial'; // тільки для часткових — але це може статися й з ідеально виконаною позицією в 2х звітах, тому дозволимо
    // Правило Максима: напівоплата тільки для часткового виконання
    if (execution === 'full' && payment === 'partial') payment = 'none';
    return { execution, payment };
  };

  // Реальні лічильники для mode selector — узгоджені з фільтром позицій preliminary
  const allPrelimItems = preliminary ? (preliminary.items || []).flatMap(s => s.items || []).filter(i => !i.hidden) : [];
  const allCount = allPrelimItems.length;
  const doneCount = allPrelimItems.filter(i => {
    const st = getPreliminaryItemStatus(i);
    return st.execution === 'full' || st.execution === 'partial';
  }).length;
  const paidCount = allPrelimItems.filter(i => {
    const st = getPreliminaryItemStatus(i);
    return st.payment === 'full' || st.payment === 'partial';
  }).length;

  return (
    <div className="min-h-screen bg-gray-50">
      <style>{hideSpinnersStyle}</style>
      
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="logo-text text-lg">LIHTAR</span>
            <span className="text-xs text-gray-400 hidden sm:inline">• кошторис проєкту</span>
          </div>
          <span className="text-[10px] text-gray-400 flex items-center gap-1">
            <Lock className="w-3 h-3" />
            {shareData?.is_locked ? 'Зафіксовано · захищене посилання' : 'Захищене посилання'}
          </span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-4 sm:py-6 space-y-4">
        
        {/* Брендинг-блок — показуємо якщо є хоч якісь бренд-дані */}
        {shareData?.brand_settings && (
          shareData.brand_settings.logo ||
          shareData.brand_settings.companyName ||
          shareData.brand_settings.managerName ||
          shareData.brand_settings.phone ||
          shareData.brand_settings.email
        ) && (
          <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
            <div className="px-4 sm:px-6 py-4 sm:py-5">
              <div className="flex items-center gap-4">
                {/* Логотип — гнучкий контейнер: мін-висота 56px, макс-ширина 200px, object-contain */}
                <div className="flex-shrink-0 flex items-center justify-center" style={{ minHeight: 56, maxWidth: 200 }}>
                  {shareData.brand_settings.logo ? (
                    <img src={shareData.brand_settings.logo} alt="" style={{ maxHeight: 72, maxWidth: 200, objectFit: 'contain' }} />
                  ) : (
                    <div className="text-[10px] text-gray-400 border border-dashed border-gray-300 rounded px-3 py-2 text-center" style={{ minWidth: 80 }}>
                      Логотип
                    </div>
                  )}
                </div>
                {/* Контакти по правому краю */}
                <div className="flex-1 text-right">
                  {shareData.brand_settings.companyName && (
                    <div className="text-sm sm:text-base font-semibold" style={{ color: '#163248' }}>{shareData.brand_settings.companyName}</div>
                  )}
                  {shareData.brand_settings.managerName && (
                    <div className="text-xs sm:text-sm text-gray-500 mt-0.5">{shareData.brand_settings.managerName}</div>
                  )}
                  <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-1 mt-1 text-xs sm:text-sm text-gray-500">
                    {shareData.brand_settings.phone && (
                      <a href={`tel:${shareData.brand_settings.phone}`} className="flex items-center gap-1 hover:text-gray-700">
                        <Phone className="w-3 h-3" />{shareData.brand_settings.phone}
                      </a>
                    )}
                    {shareData.brand_settings.email && (
                      <a href={`mailto:${shareData.brand_settings.email}`} className="flex items-center gap-1 hover:text-gray-700">
                        <Mail className="w-3 h-3" />{shareData.brand_settings.email}
                      </a>
                    )}
                    {shareData.brand_settings.website && (
                      <a href={shareData.brand_settings.website.startsWith('http') ? shareData.brand_settings.website : `https://${shareData.brand_settings.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-gray-600">
                        <ExternalLink className="w-3 h-3" />{shareData.brand_settings.website.replace(/^https?:\/\//, '')}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Картка проєкту — без objectType icon-box, назва + мета в одну строку з · роздільниками */}
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <div className="px-4 sm:px-6 py-4 sm:py-5">
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold" style={{ color: '#163248' }}>
                {project.name || 'Без назви'}
              </h1>
              <div className="mt-1.5 text-sm text-gray-500 space-y-0.5">
                {project.address && <div>{project.address}</div>}
                {project.objectType && <div>{objectType.label}</div>}
                {project.area > 0 && <div>{project.area} м²</div>}
              </div>
            </div>
          </div>
        </div>

        {/* ═══ DASHBOARD ═══ */}

        {/* Прогрес-картка */}
        {totalItemsCount > 0 && (
          <div className="bg-white border border-gray-200 rounded-xl px-5 py-4">
            <div className="flex items-baseline justify-between mb-2.5">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                Виконано робіт
              </div>
              <div className="text-2xl font-bold" style={{ color: '#163248' }}>{workPercent}%</div>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${Math.min(workPercent, 100)}%`, backgroundColor: '#163248' }} />
            </div>
          </div>
        )}

        {/* Огляд — 3 клікабельні картки */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-3">Огляд</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
            {/* Планові витрати за попереднім кошторисом */}
            <button
              onClick={scrollToPreliminary}
              className="text-left bg-white border border-gray-200 rounded-xl p-4 hover:border-gray-400 hover:shadow-sm transition-all"
            >
              <div className="text-[11px] text-gray-500 font-medium leading-snug mb-2" style={{ minHeight: 30 }}>
                Планові витрати за попереднім кошторисом
              </div>
              <div className="text-xl font-bold tabular-nums text-gray-500">
                {fmt(budgetPreliminary)} грн
              </div>
            </button>

            {/* Загальна вартість виконаних робіт */}
            <button
              onClick={openReportsModal}
              className="text-left bg-white border border-gray-200 rounded-xl p-4 hover:border-gray-400 hover:shadow-sm transition-all"
            >
              <div className="text-[11px] text-gray-500 font-medium leading-snug mb-2" style={{ minHeight: 30 }}>
                Загальна вартість виконаних робіт (за які відзвітовано)
              </div>
              <div className="text-xl font-bold tabular-nums" style={{ color: '#163248' }}>
                {fmt(totalExpenses)} грн
              </div>
            </button>

            {/* Сума всіх оплат — колір залежить від балансу */}
            <button
              onClick={scrollToPayments}
              className="text-left bg-white border border-gray-200 rounded-xl p-4 hover:border-gray-400 hover:shadow-sm transition-all"
            >
              <div className="text-[11px] text-gray-500 font-medium leading-snug mb-2" style={{ minHeight: 30 }}>
                Сума всіх оплат зроблених клієнтом
              </div>
              <div className="text-xl font-bold tabular-nums" style={{ color: balance >= 0 ? '#22c55e' : '#dc2626' }}>
                {fmt(totalReceived)} грн
              </div>
            </button>
          </div>
        </div>

        {/* Вартість м² + Donut розподіл витрат */}
        {(area > 0 || catTotal > 0) && (
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-3">Вартість м²</div>
            <div className={`grid gap-3 ${(area > 0 && catTotal > 0) ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
              {/* Ліва колонка — 2 картки вартості м² */}
              {area > 0 && (
                <div className="flex flex-col gap-2.5">
                  <div className="bg-white border border-gray-200 rounded-xl px-4 py-3">
                    <div className="text-[11px] text-gray-500 font-medium mb-1.5">Планова вартість м²</div>
                    <div className="text-lg font-bold tabular-nums" style={{ color: '#163248' }}>
                      {fmt(plannedCostPerM2)} <span className="text-xs font-medium text-gray-500">грн/м²</span>
                    </div>
                  </div>
                  <div className="bg-white border border-gray-200 rounded-xl px-4 py-3">
                    <div className="text-[11px] text-gray-500 font-medium mb-1.5">Поточна вартість м²</div>
                    <div className="text-lg font-bold tabular-nums flex items-baseline gap-2" style={{ color: '#163248' }}>
                      <span>{fmt(currentCostPerM2)} <span className="text-xs font-medium text-gray-500">грн/м²</span></span>
                      {m2Delta !== 0 && plannedCostPerM2 > 0 && (
                        <span
                          className="text-[11px] font-semibold px-1.5 py-0.5 rounded"
                          style={
                            m2Delta < 0
                              ? { background: '#dcfce7', color: '#166534' }
                              : { background: '#fee2e2', color: '#991b1b' }
                          }
                        >
                          {m2Delta < 0 ? '−' : '+'}{fmt(Math.abs(m2Delta))}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {/* Права колонка — Donut розподіл фактичних витрат */}
              {catTotal > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <div className="text-[11px] text-gray-500 font-medium mb-3">Розподіл фактичних витрат</div>
                  <div className="flex items-center gap-4 flex-col sm:flex-row">
                    <div
                      className="flex-shrink-0"
                      style={{
                        width: 110,
                        height: 110,
                        borderRadius: '50%',
                        position: 'relative',
                        background: `conic-gradient(${donutBg})`
                      }}
                      aria-hidden="true"
                    >
                      <div style={{ position: 'absolute', top: '22%', left: '22%', width: '56%', height: '56%', background: '#fff', borderRadius: '50%' }} />
                    </div>
                    <div className="flex-1 min-w-0 w-full flex flex-col gap-1.5">
                      {catSegments.map(seg => (
                        <div key={seg.name} className="flex items-center gap-2 text-xs">
                          <span style={{ width: 10, height: 10, borderRadius: '50%', background: seg.color, flexShrink: 0 }} />
                          <span className="flex-1 text-gray-700 truncate">{seg.name}</span>
                          <span className="font-bold tabular-nums" style={{ color: '#163248' }}>{seg.pct.toFixed(1)}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Розподіл (план / поточне) — 4 картки з прогрес-барами */}
        {hasAnyDistribution && (
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-3">Розподіл (план / поточне)</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {categoriesForBar.map(cat => {
                const IconComp = cat.icon;
                const over = cat.plan > 0 && cat.current > cat.plan;
                const max = Math.max(cat.plan, cat.current) || 1;
                const planPct = cat.plan > 0 ? (Math.min(cat.plan, cat.current) / max) * 100 : 0;
                const overPct = over ? ((cat.current - cat.plan) / max) * 100 : (cat.plan === 0 && cat.current > 0 ? 100 : 0);
                const tailPct = Math.max(0, 100 - planPct - overPct);
                return (
                  <div key={cat.name} className="bg-white border border-gray-200 rounded-xl p-3.5">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0" style={{ color: '#163248' }}>
                        <IconComp className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] text-gray-500 font-medium">{cat.name}</div>
                        <div className="text-base font-bold tabular-nums" style={{ color: over ? '#dc2626' : '#163248' }}>
                          {fmt(cat.current)} грн
                        </div>
                      </div>
                    </div>
                    <div className="h-1 bg-gray-200 rounded-sm overflow-hidden flex mb-1.5">
                      {planPct > 0 && <div style={{ width: `${planPct}%`, background: '#163248' }} />}
                      {overPct > 0 && <div style={{ width: `${overPct}%`, background: '#dc2626' }} />}
                      {tailPct > 0 && <div style={{ width: `${tailPct}%` }} />}
                    </div>
                    <div className="text-[11px] text-gray-500">
                      Планові витрати: <span className="tabular-nums text-gray-700 font-medium">{fmt(cat.plan)} грн</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Баланс */}
        {(totalExpenses > 0 || totalReceived > 0) && (
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-3">Баланс</div>
            <div className="flex items-start justify-between gap-3 py-1">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold" style={{ color: '#163248' }}>Баланс</div>
                <div className="text-xs text-gray-500 mt-0.5">різниця між сумою всіх оплат і сумою всіх звітів</div>
              </div>
              <div className="text-lg font-bold tabular-nums" style={{ color: balance >= 0 ? '#22c55e' : '#dc2626' }}>
                {balance >= 0 ? '+' : '−'}{fmt(Math.abs(balance))} грн
              </div>
            </div>
          </div>
        )}

        {/* Транзакції */}
        {allPayments.length > 0 && (
          <div id="payments-block" className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-3">Транзакції</div>
            {allPayments.map((payment, pIdx, arr) => (
              <div
                key={payment.id || pIdx}
                className={`flex items-center gap-3 py-3 ${pIdx < arr.length - 1 ? 'border-b border-gray-100' : ''}`}
              >
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#dcfce7', color: '#22c55e' }}>
                  <CheckCircle2 className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold" style={{ color: '#163248' }}>
                    {new Date(payment.date).toLocaleDateString('uk-UA', { day: '2-digit', month: 'long', year: 'numeric' })}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">внесено виконробом{payment.note ? ` · ${payment.note}` : ''}</div>
                </div>
                <div className="text-sm font-bold tabular-nums text-green-600 flex-shrink-0">
                  +{fmt(payment.amount || 0)} грн
                </div>
              </div>
            ))}
            <div className="flex justify-between items-center pt-3 mt-2 border-t-2 border-gray-200">
              <span className="text-sm font-bold" style={{ color: '#163248' }}>Разом отримано</span>
              <span className="text-lg font-bold tabular-nums" style={{ color: '#163248' }}>{fmt(totalReceived)} грн</span>
            </div>
          </div>
        )}

        {/* Кнопка Звіти */}
        {reports.length > 0 && (
          <button
            onClick={openReportsModal}
            className="w-full py-4 rounded-xl text-white font-semibold text-sm flex items-center justify-center gap-2.5 transition-opacity hover:opacity-90"
            style={{ background: '#163248' }}
          >
            <FileText className="w-4 h-4" />
            Звіти
          </button>
        )}

        {/* ═══ / DASHBOARD ═══ */}

        {/* Mode Selector: Що зроблено / За що заплачено / Показати все */}
        <div className="bg-white border border-gray-200 rounded-xl p-1.5 flex gap-1">
          <button
            onClick={() => setMode('done')}
            className={`flex-1 py-2 px-2 rounded-lg text-xs font-medium flex items-center justify-center gap-1.5 transition-colors ${mode === 'done' ? 'text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            style={mode === 'done' ? { background: '#163248' } : {}}
          >
            <span className="w-2 h-2 rounded-full" style={{ background: '#22c55e' }} />
            <span>Що зроблено</span>
            <span className="opacity-70">· {doneCount}</span>
          </button>
          <button
            onClick={() => setMode('paid')}
            className={`flex-1 py-2 px-2 rounded-lg text-xs font-medium flex items-center justify-center gap-1.5 transition-colors ${mode === 'paid' ? 'text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            style={mode === 'paid' ? { background: '#163248' } : {}}
          >
            <span className="w-2 h-2 rounded-full" style={{ background: '#f97316' }} />
            <span>За що заплачено</span>
            <span className="opacity-70">· {paidCount}</span>
          </button>
          <button
            onClick={() => setMode('all')}
            className={`flex-1 py-2 px-2 rounded-lg text-xs font-medium flex items-center justify-center gap-1.5 transition-colors ${mode === 'all' ? 'text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            style={mode === 'all' ? { background: '#163248' } : {}}
          >
            <span className="w-2 h-2 rounded-full" style={{ background: '#a3a3a3' }} />
            <span>Показати все</span>
            <span className="opacity-70">· {allCount}</span>
          </button>
        </div>

        {/* Кошториси / Звіти */}
        {visibleEstimates.map((estimate) => {
          const totals = calcEstimateTotals(estimate);
          const isOpen = expandedEstimates[estimate.id] !== false; // відкриті за замовч
          const payments = getEstimatePayments(estimate);
          const received = getEstimateReceived(estimate);
          const atsEnabled = estimate.atsEnabled ?? true;
          const atsPercent = estimate.atsPercent ?? 5;

          return (
            <div
              key={estimate.id}
              id={estimate.isPreliminary ? 'preliminary-block' : `estimate-${estimate.id}`}
              className="bg-white rounded-xl border shadow-sm overflow-hidden"
            >
              {/* Шапка кошторису */}
              <div 
                className="px-4 sm:px-6 py-3 sm:py-4 cursor-pointer flex items-center justify-between"
                style={{ backgroundColor: estimate.isPreliminary ? '#163248' : 'white' }}
                onClick={() => toggleEstimate(estimate.id)}
              >
                <div className="flex items-center gap-2 min-w-0">
                  {isOpen 
                    ? <ChevronDown className={`w-4 h-4 flex-shrink-0 ${estimate.isPreliminary ? 'text-white/60' : 'text-gray-400'}`} />
                    : <ChevronRight className={`w-4 h-4 flex-shrink-0 ${estimate.isPreliminary ? 'text-white/60' : 'text-gray-400'}`} />
                  }
                  <h2 className={`font-semibold truncate ${estimate.isPreliminary ? 'text-white' : ''}`} style={!estimate.isPreliminary ? { color: '#163248' } : {}}>
                    {estimate.isPreliminary ? 'Попередній кошторис' : estimate.name}
                  </h2>
                </div>
                <span className={`font-bold tabular-nums flex-shrink-0 ml-2 ${estimate.isPreliminary ? 'text-white' : ''}`} style={!estimate.isPreliminary ? { color: '#163248' } : {}}>
                  {totals.grandTotal.toLocaleString('uk-UA', { maximumFractionDigits: 0 })} ₴
                </span>
              </div>

              {isOpen && (
                <div>
                  {/* Банер "Разом за попереднім кошторисом" — тільки в preliminary */}
                  {estimate.isPreliminary && (
                    <div className="px-4 sm:px-6 py-3 flex flex-col gap-1.5 border-t border-white/10" style={{ background: '#1e4059' }}>
                      <div className="flex justify-between items-baseline gap-3 text-white">
                        <span className="text-sm font-bold">Планові витрати за попереднім кошторисом</span>
                        <span className="text-base font-bold tabular-nums whitespace-nowrap">{fmt(preliminaryPlanCost)} грн</span>
                      </div>
                      <div className="flex justify-between items-baseline gap-3 pt-2 border-t border-white/15">
                        <span className="text-[13px] text-white/85">
                          Фактичні витрати{prelimAtsEnabled && prelimAtsPercent > 0 ? ' (з АТС)' : ''}
                        </span>
                        <span className="text-sm font-bold tabular-nums whitespace-nowrap text-white">{fmt(preliminaryFactCost)} грн</span>
                      </div>
                    </div>
                  )}

                  {/* Розділи */}
                  {(estimate.items || []).map((section, sIdx) => {
                    const st = calcSectionTotals(section);
                    const sectionKey = `${estimate.id}-${section.id}`;
                    const isSectionOpen = expandedSections[sectionKey] !== false;
                    // Фільтр за mode: 'all' — всі, 'done' — тільки виконані, 'paid' — тільки оплачені
                    const visibleItems = (section.items || []).filter(item => {
                      if (item.hidden) return false;
                      if (!estimate.isPreliminary) return true; // для звітів без фільтру (хоча звіти зараз не в списку)
                      if (mode === 'all') return true;
                      const st = getPreliminaryItemStatus(item);
                      if (mode === 'done') return st.execution === 'full' || st.execution === 'partial';
                      if (mode === 'paid') return st.payment === 'full' || st.payment === 'partial';
                      return true;
                    });
                    // Секція повністю прихована якщо в ній жодної видимої позиції в цьому режимі
                    if (visibleItems.length === 0) return null;

                    // Для preliminary — сума з АТС + окремо АТС
                    const secWithATS = estimate.isPreliminary ? getSectionCostWithATS(section) : { clientCost: st.clientCost, atsAmount: 0, total: st.clientCost };
                    const showATSInHeader = estimate.isPreliminary && atsEnabled && (section.includeATS ?? true) && secWithATS.atsAmount > 0;

                    return (
                      <div key={section.id} className="border-t">
                        {/* Шапка розділу — світло-сіра, чиста */}
                        <div
                          className="px-4 sm:px-6 py-3 cursor-pointer active:bg-gray-100 border-b border-gray-200"
                          style={{ backgroundColor: '#fafafa' }}
                          onClick={() => toggleSection(estimate.id, section.id)}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              {isSectionOpen
                                ? <ChevronDown className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" />
                                : <ChevronRight className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" />
                              }
                              <span className="text-sm font-semibold" style={{ color: '#163248', wordBreak: 'break-word', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                {sIdx + 1}. {section.name}
                              </span>
                              <span className="text-xs text-gray-400">({visibleItems.length})</span>
                              {atsEnabled && (section.includeATS ?? true) && (
                                <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-gray-200 text-gray-700 uppercase tracking-wider flex-shrink-0">
                                  АТС
                                </span>
                              )}
                            </div>
                            <span className="text-sm font-bold tabular-nums flex-shrink-0" style={{ color: '#163248' }}>
                              {(estimate.isPreliminary ? secWithATS.total : st.clientCost).toLocaleString('uk-UA', { maximumFractionDigits: 0 })} ₴
                            </span>
                          </div>
                          {showATSInHeader && (
                            <div className="flex items-center justify-between gap-2 mt-1 pl-6">
                              <span className="text-[11px] text-gray-500">в т.ч. АТС {prelimAtsPercent}%</span>
                              <span className="text-[11px] font-medium tabular-nums text-gray-600">
                                {Math.round(secWithATS.atsAmount).toLocaleString('uk-UA')} ₴
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Позиції */}
                        {isSectionOpen && visibleItems.length > 0 && (
                          <div className="divide-y divide-gray-100">
                            {visibleItems.map((item) => {
                              const cp = item.clientPrice !== undefined
                                ? item.clientPrice
                                : Math.round((item.workPrice || 0) * (1 + (item.markup || 0) / 100) * 100) / 100;
                              const planTotal = Math.round((cp || 0) * (item.qty || 0) * 100) / 100;
                              const typeConfig = ITEM_TYPES[item.type] || ITEM_TYPES['Робота'];

                              // ═══ Для звітів — старий простий рендер ═══
                              if (!estimate.isPreliminary) {
                                return (
                                  <div key={item.id} className="flex items-start px-4 sm:px-6 py-2">
                                    <span
                                      className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0 mr-2 mt-0.5"
                                      style={{ backgroundColor: {'Робота':'#22c55e','Матеріали':'#eab308','Комплектація':'#3b82f6','Транспорт':'#f97316','Інші послуги':'#a855f7'}[item.type] || '#9ca3af' }}
                                    >{typeConfig.letter}</span>
                                    <div className="flex-1 min-w-0 mr-2">
                                      <p className="text-sm" style={{ color: '#163248', wordBreak: 'break-word', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                        {item.position || 'Без назви'}
                                      </p>
                                      <div className="flex items-center gap-2 mt-0.5">
                                        <span className="text-xs text-gray-400">{item.qty || 0} {item.unit}</span>
                                        <span className="text-xs text-gray-400">× {cp.toLocaleString('uk-UA', { maximumFractionDigits: 0 })} ₴</span>
                                      </div>
                                    </div>
                                    <span className="text-sm tabular-nums flex-shrink-0" style={{ color: '#163248' }}>
                                      {planTotal.toLocaleString('uk-UA', { maximumFractionDigits: 0 })} ₴
                                    </span>
                                  </div>
                                );
                              }

                              // ═══ Для preliminary — 3-рядковий формат ═══
                              const prStatus = getPreliminaryItemStatus(item);
                              const factData = getPreliminaryItemFactData(item);
                              const hasLinks = (item.links || []).length > 0;
                              const factCost = factData.factCost;
                              const factQty = factData.factQty;
                              const delta = factCost - planTotal;
                              const remaining = (item.qty || 0) - factQty;

                              // Крапки статусу
                              const dotBase = { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 };
                              const executionDot =
                                prStatus.execution === 'full' ? { ...dotBase, background: '#22c55e' } :
                                prStatus.execution === 'partial' ? { ...dotBase, background: 'linear-gradient(to top, #22c55e 50%, #d1d5db 50%)' } :
                                null;
                              const paymentDot =
                                prStatus.payment === 'full' ? { ...dotBase, background: '#f97316' } :
                                prStatus.payment === 'partial' ? { ...dotBase, background: 'linear-gradient(to top, #f97316 50%, #d1d5db 50%)' } :
                                null;

                              // Прогрес для часткової
                              const showProgress = prStatus.execution === 'partial' && (item.qty || 0) > 0;
                              const progressPct = showProgress ? Math.min(100, Math.round((factQty / (item.qty || 1)) * 100)) : 0;

                              return (
                                <div key={item.id} className="px-4 sm:px-6 py-2.5">
                                  {/* Row 1: крапки → type-badge → назва | планова сума */}
                                  <div className="flex items-center gap-2">
                                    <div className="flex flex-col gap-0.5 flex-shrink-0" style={{ minWidth: 10 }}>
                                      {executionDot && <span style={executionDot} title={prStatus.execution === 'full' ? 'Виконано' : 'Виконано частково'} />}
                                      {paymentDot && <span style={paymentDot} title={prStatus.payment === 'full' ? 'Оплачено' : 'Оплачено частково'} />}
                                    </div>
                                    <span
                                      className="w-4 h-4 rounded-full flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0"
                                      style={{ backgroundColor: {'Робота':'#22c55e','Матеріали':'#eab308','Комплектація':'#3b82f6','Транспорт':'#f97316','Інші послуги':'#a855f7'}[item.type] || '#9ca3af' }}
                                    >{typeConfig.letter}</span>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm" style={{ color: '#163248', wordBreak: 'break-word' }}>{item.position || 'Без назви'}</p>
                                    </div>
                                    <span className="text-sm font-bold tabular-nums flex-shrink-0 whitespace-nowrap" style={{ color: '#163248' }}>
                                      {fmt(planTotal)} грн
                                    </span>
                                  </div>

                                  {/* Row 2: план N × ціна */}
                                  <div className="flex items-baseline gap-2 mt-1 pl-8 text-xs text-gray-500 tabular-nums">
                                    <span>план {item.qty || 0} {item.unit || ''} × {fmt(cp)} грн</span>
                                  </div>

                                  {/* Row 3: факт / "Ще не виконано" */}
                                  <div className="flex items-baseline justify-between gap-2 mt-0.5 pl-8">
                                    {hasLinks ? (
                                      <>
                                        <span className="text-xs text-gray-500 tabular-nums">факт {factQty} {item.unit || ''} × {fmt(factQty > 0 ? factCost / factQty : cp)} грн</span>
                                        <span className="text-xs font-medium tabular-nums text-gray-700 whitespace-nowrap">
                                          {fmt(factCost)} грн
                                          {prStatus.execution === 'partial' && remaining > 0 ? (
                                            <span className="ml-1 text-gray-400 font-normal">(залишилось {remaining} {item.unit || ''})</span>
                                          ) : delta !== 0 && prStatus.execution === 'full' ? (
                                            <span
                                              className="ml-1 font-semibold"
                                              style={{ color: delta > 0 ? '#dc2626' : '#22c55e' }}
                                            >
                                              ({delta > 0 ? '+' : '−'}{fmt(Math.abs(delta))})
                                            </span>
                                          ) : null}
                                        </span>
                                      </>
                                    ) : item.completed ? (
                                      <span className="text-xs text-gray-500">Виконано без прив'язки до звіту</span>
                                    ) : (
                                      <span className="text-xs text-gray-400">Ще не виконано</span>
                                    )}
                                  </div>

                                  {/* Прогрес-бар для часткової */}
                                  {showProgress && (
                                    <div className="pl-8 mt-1.5">
                                      <div className="h-1 rounded overflow-hidden" style={{ background: '#e5e7eb' }}>
                                        <div className="h-full rounded" style={{ width: `${progressPct}%`, background: '#22c55e' }} />
                                      </div>
                                    </div>
                                  )}

                                  {/* Бейджі linked звітів — клікабельні, відкривають модалку звіту */}
                                  {hasLinks && factData.linkedItems.length > 0 && (
                                    <div className="pl-8 mt-2 flex flex-wrap gap-1.5">
                                      {(item.links || []).map((link, idx) => {
                                        const li = factData.linkedItems[idx];
                                        if (!li) return null;
                                        return (
                                          <button
                                            key={idx}
                                            onClick={() => openReportById(link.estimateId)}
                                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-gray-200 bg-gray-50 text-[11px] text-gray-600 hover:bg-gray-100 hover:border-gray-300 hover:text-gray-900 transition-colors cursor-pointer"
                                          >
                                            <FileText className="w-2.5 h-2.5" />
                                            {li.estName}{prStatus.execution === 'partial' ? ` · ${li.qty} ${item.unit || ''}` : ''}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Документи */}
                  {(estimate.documents || []).filter(d => d.visible !== false).length > 0 && (
                    <div className="border-t px-4 sm:px-6 py-3">
                      <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Документи</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {(estimate.documents || []).filter(d => d.visible !== false).map((doc, dIdx) => (
                          <a
                            key={doc.id || dIdx}
                            href={doc.webViewLink || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 p-2 rounded-lg border hover:bg-gray-50 transition-colors text-sm"
                          >
                            {doc.type?.startsWith('image/') ? (
                              <Image className="w-4 h-4 text-blue-500 flex-shrink-0" />
                            ) : (
                              <FileText className="w-4 h-4 text-red-500 flex-shrink-0" />
                            )}
                            <span className="truncate text-xs text-gray-700">{doc.name}</span>
                            <ExternalLink className="w-3 h-3 text-gray-400 flex-shrink-0 ml-auto" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* ═══ ДОДАТКОВІ РОБОТИ ═══
             Автоматично: позиції в звітах які НЕ прив'язані до жодної preliminary — показуємо як додаткові.
             Плоский список з бейджем "Звіт №N" на кожній позиції. */}
        {(() => {
          // Збираємо всі linked itemId по звітах (з preliminary items links)
          const linkedByReport = {}; // { estimateId: Set<itemId> }
          (preliminary?.items || []).forEach(sec => {
            (sec.items || []).forEach(it => {
              (it.links || []).forEach(l => {
                if (!linkedByReport[l.estimateId]) linkedByReport[l.estimateId] = new Set();
                linkedByReport[l.estimateId].add(l.itemId);
              });
            });
          });
          // Для кожного звіту — позиції що НЕ прив'язані
          const extraItems = [];
          reports.forEach(est => {
            const linkedIds = linkedByReport[est.id] || new Set();
            (est.items || []).forEach(sec => {
              (sec.items || []).forEach(it => {
                if (it.hidden) return;
                if (!linkedIds.has(it.id)) {
                  extraItems.push({ item: it, estimate: est, section: sec });
                }
              });
            });
          });
          if (extraItems.length === 0) return null;
          // Показуємо тільки в mode='all' (додаткові — це план+факт, логічно в загальному режимі)
          if (mode !== 'all') return null;
          return (
            <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
              <div className="px-4 sm:px-6 py-3 flex items-center justify-between gap-3 border-b border-gray-200" style={{ background: '#fafafa' }}>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold" style={{ color: '#163248' }}>Додаткові роботи</span>
                  <span className="text-xs text-gray-400">({extraItems.length})</span>
                </div>
                <span className="text-sm font-bold tabular-nums" style={{ color: '#dc2626' }}>
                  +{fmt(extraItems.reduce((s, { item }) => {
                    const cp = item.clientPrice !== undefined ? item.clientPrice : Math.round((item.workPrice || 0) * (1 + (item.markup || 0) / 100) * 100) / 100;
                    return s + (item.qty || 0) * cp;
                  }, 0))} грн
                </span>
              </div>
              <div className="divide-y divide-gray-100" style={{ borderLeft: '3px solid #163248' }}>
                {extraItems.map(({ item, estimate, section }, idx) => {
                  const cp = item.clientPrice !== undefined ? item.clientPrice : Math.round((item.workPrice || 0) * (1 + (item.markup || 0) / 100) * 100) / 100;
                  const total = (item.qty || 0) * cp;
                  const typeConfig = ITEM_TYPES[item.type] || ITEM_TYPES['Робота'];
                  return (
                    <div key={`${estimate.id}-${section.id}-${item.id}-${idx}`} className="px-4 sm:px-6 py-2.5">
                      <div className="flex items-center gap-2">
                        <span
                          className="w-4 h-4 rounded-full flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0"
                          style={{ backgroundColor: {'Робота':'#22c55e','Матеріали':'#eab308','Комплектація':'#3b82f6','Транспорт':'#f97316','Інші послуги':'#a855f7'}[item.type] || '#9ca3af' }}
                        >{typeConfig.letter}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm" style={{ color: '#163248', wordBreak: 'break-word' }}>{item.position || 'Без назви'}</p>
                        </div>
                        <span className="text-sm font-bold tabular-nums flex-shrink-0 whitespace-nowrap" style={{ color: '#dc2626' }}>
                          +{fmt(total)} грн
                        </span>
                      </div>
                      <div className="flex items-baseline justify-between gap-2 mt-1 pl-6">
                        <span className="text-xs text-gray-500 tabular-nums">{item.qty || 0} {item.unit || ''} × {fmt(cp)} грн</span>
                        <button
                          onClick={() => openReportById(estimate.id)}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-gray-200 bg-gray-50 text-[11px] text-gray-600 hover:bg-gray-100 hover:border-gray-300 hover:text-gray-900 transition-colors cursor-pointer"
                        >
                          <FileText className="w-2.5 h-2.5" />
                          {estimate.name}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* ═══ МОДАЛКА СПИСКУ ЗВІТІВ ═══ */}
        {reportsListOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-40 p-0 sm:p-4" onClick={() => setReportsListOpen(false)}>
            <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[92vh] flex flex-col overflow-hidden shadow-2xl" style={{ overscrollBehavior: 'contain' }} onClick={e => e.stopPropagation()}>
              <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-bold text-base" style={{ color: '#163248' }}>Звіти</h3>
                  <p className="text-xs text-gray-500 mt-0.5">{reports.length} {reports.length === 1 ? 'звіт' : reports.length < 5 ? 'звіти' : 'звітів'}</p>
                </div>
                <button onClick={() => setReportsListOpen(false)} className="text-gray-400 hover:text-gray-700"><Lock className="w-5 h-5" style={{ display: 'none' }} /><span className="text-2xl leading-none" style={{ fontWeight: 300 }}>×</span></button>
              </div>
              <div className="flex-1 overflow-y-auto p-2">
                {reports.slice().sort((a, b) => (a.id || 0) - (b.id || 0)).map(est => {
                  const t = calcEstimateTotals(est);
                  const received = estimateFifoReceivedMap[est.id] || 0;
                  const isCovered = estimateCoverageMap[est.id];
                  return (
                    <button
                      key={est.id}
                      onClick={() => openReportById(est.id)}
                      className="w-full text-left px-3 py-3 rounded-lg hover:bg-gray-50 flex items-center justify-between gap-3 border-b border-gray-100 last:border-b-0"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm" style={{ color: '#163248' }}>{est.name}</div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {(est.items || []).reduce((s, sec) => s + (sec.items || []).filter(i => !i.hidden).length, 0)} позицій
                          {isCovered && <span className="ml-2 inline-flex items-center gap-1 text-green-600 font-medium"><span className="w-1.5 h-1.5 rounded-full bg-green-500"/>оплачено</span>}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-sm font-bold tabular-nums" style={{ color: '#163248' }}>{fmt(t.grandTotal)} грн</div>
                        <div className="text-[11px] text-gray-500 tabular-nums mt-0.5">оплачено {fmt(received)}</div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ═══ МОДАЛКА КОНКРЕТНОГО ЗВІТУ ═══ */}
        {reportModalId !== null && (() => {
          const est = reports.find(e => e.id === reportModalId);
          if (!est) return null;
          const t = calcEstimateTotals(est);
          const received = estimateFifoReceivedMap[est.id] || 0;
          const isCovered = estimateCoverageMap[est.id];
          const paymentPct = t.grandTotal > 0 ? Math.min(100, Math.round((received / t.grandTotal) * 100)) : 0;
          const atsEnabled = est.atsEnabled ?? true;
          const atsPercent = est.atsPercent ?? 5;
          return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-40 p-0 sm:p-4" onClick={() => setReportModalId(null)}>
              <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden shadow-2xl" style={{ overscrollBehavior: 'contain' }} onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    {reports.length > 1 && (
                      <button
                        onClick={() => { setReportModalId(null); setReportsListOpen(true); }}
                        className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 mb-1.5 -ml-1 px-1 py-0.5 rounded transition-colors"
                      >
                        <ChevronRight className="w-3 h-3" style={{ transform: 'rotate(180deg)' }} />
                        До всіх звітів
                      </button>
                    )}
                    <h3 className="font-bold text-base" style={{ color: '#163248' }}>{est.name}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {(est.items || []).reduce((s, sec) => s + (sec.items || []).filter(i => !i.hidden).length, 0)} позицій
                    </p>
                  </div>
                  <button onClick={() => setReportModalId(null)} className="text-gray-400 hover:text-gray-700 flex-shrink-0"><span className="text-2xl leading-none" style={{ fontWeight: 300 }}>×</span></button>
                </div>

                {/* Компактний блок оплати */}
                <div className="px-5 py-3 border-b border-gray-100" style={{ background: '#fafafa' }}>
                  {isCovered ? (
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-green-500"/>
                      <span className="text-sm font-semibold text-green-600">Повністю оплачено</span>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-baseline justify-between mb-1.5">
                        <span className="text-xs text-gray-500">Оплачено</span>
                        <span className="text-sm font-semibold tabular-nums" style={{ color: '#163248' }}>
                          {fmt(received)} з {fmt(t.grandTotal)} грн
                        </span>
                      </div>
                      <div className="h-1.5 bg-gray-200 rounded overflow-hidden">
                        <div className="h-full rounded" style={{ width: `${paymentPct}%`, background: '#22c55e' }} />
                      </div>
                    </>
                  )}
                </div>

                {/* Тіло — розділи з позиціями */}
                <div className="flex-1 overflow-y-auto">
                  {(est.items || []).map((section, sIdx) => {
                    const st = calcSectionTotals(section);
                    const visibleItems = (section.items || []).filter(i => !i.hidden);
                    if (visibleItems.length === 0) return null;
                    return (
                      <div key={section.id} className="border-b border-gray-100 last:border-b-0">
                        {/* Шапка розділу */}
                        <div className="px-5 py-2.5 flex items-center justify-between gap-2" style={{ background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <span className="text-sm font-semibold" style={{ color: '#163248' }}>{sIdx + 1}. {section.name}</span>
                            <span className="text-xs text-gray-400">({visibleItems.length})</span>
                            {atsEnabled && (section.includeATS ?? true) && (
                              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-gray-200 text-gray-700 uppercase tracking-wider flex-shrink-0">АТС</span>
                            )}
                          </div>
                          <span className="text-sm font-bold tabular-nums flex-shrink-0" style={{ color: '#163248' }}>
                            {fmt(st.clientCost)} грн
                          </span>
                        </div>
                        {/* Позиції */}
                        <div className="divide-y divide-gray-50">
                          {visibleItems.map(item => {
                            const cp = item.clientPrice !== undefined
                              ? item.clientPrice
                              : Math.round((item.workPrice || 0) * (1 + (item.markup || 0) / 100) * 100) / 100;
                            const total = (item.qty || 0) * cp;
                            const typeConfig = ITEM_TYPES[item.type] || ITEM_TYPES['Робота'];
                            return (
                              <div key={item.id} className="px-5 py-2.5 flex items-start justify-between gap-3">
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                  <span
                                    className="w-4 h-4 rounded-full flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0"
                                    style={{ backgroundColor: {'Робота':'#22c55e','Матеріали':'#eab308','Комплектація':'#3b82f6','Транспорт':'#f97316','Інші послуги':'#a855f7'}[item.type] || '#9ca3af' }}
                                  >{typeConfig.letter}</span>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm" style={{ color: '#163248', wordBreak: 'break-word' }}>{item.position || 'Без назви'}</p>
                                    <div className="text-xs text-gray-500 tabular-nums mt-0.5">{item.qty || 0} {item.unit} × {fmt(cp)} грн</div>
                                  </div>
                                </div>
                                <span className="text-sm font-semibold tabular-nums flex-shrink-0 whitespace-nowrap" style={{ color: '#163248' }}>
                                  {fmt(total)} грн
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}

                  {/* Підсумки */}
                  <div className="px-5 py-3 space-y-1.5" style={{ background: '#fafafa' }}>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Вартість робіт/матеріалів</span>
                      <span className="font-medium tabular-nums" style={{ color: '#163248' }}>{fmt(t.totalClientCost)} грн</span>
                    </div>
                    {atsEnabled && t.atsAmount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Адміністративно-технічний супровід ({atsPercent}%)</span>
                        <span className="font-medium tabular-nums" style={{ color: '#163248' }}>{fmt(t.atsAmount)} грн</span>
                      </div>
                    )}
                  </div>
                  <div className="px-5 py-3 flex justify-between items-baseline" style={{ background: '#163248', color: '#fff' }}>
                    <span className="text-sm font-bold">Разом</span>
                    <span className="text-lg font-bold tabular-nums">{fmt(t.grandTotal)} грн</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* FAB кнопка чату */}
        <button
          onClick={() => setShowChat(true)}
          className="fixed bottom-5 right-5 w-14 h-14 rounded-full shadow-lg text-white flex items-center justify-center z-20 active:scale-95 transition-transform"
          style={{ backgroundColor: '#163248' }}
        >
          <MessageCircle className="w-6 h-6" />
        </button>

        {/* Чат модалка */}
        <ChatModal
          isOpen={showChat}
          onClose={() => setShowChat(false)}
          projectId={project?.id}
          isClientMode={true}
          shareToken={shareToken}
        />

        {/* Footer */}
        <div className="text-center py-6 text-xs text-gray-400">
          <span className="logo-text text-sm">LIHTAR</span>
          <span className="ml-1">• Управління бюджетом будівництва</span>
        </div>
      </div>
    </div>
  );
};

export default ClientViewV2;
